import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  customsDeclarations,
  type CustomsDeclarationItem,
} from '@/api/customsDeclarations';
import { goodsReceipts } from '@/api/goodsReceipts';
import { orgs, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  customs_number: string;
  customs_date: string;
  country_origin_code: string;
  customs_fee: string;
  declaration_type: 'IMPORT' | 'EXPORT';
  export_confirmation_date: string;  // ISO date or empty
  comment: string;
  items: CustomsDeclarationItem[];
  // Для UI: какое поступление выбрано для добавления строк
  source_receipt_id: string;
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  customs_number: '',
  customs_date: new Date().toISOString().slice(0, 10),
  country_origin_code: '',
  customs_fee: '0',
  declaration_type: 'IMPORT',
  export_confirmation_date: '',
  comment: '',
  items: [],
  source_receipt_id: '',
};

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CustomsDeclarationEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по purchases.receipt.create permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('purchases.receipt.create');
  const nomList = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.list({ limit: 500 }),
  });
  const vatList = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });
  const docQuery = useQuery({
    queryKey: ['customs-declaration', id],
    queryFn: () => customsDeclarations.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const numberPreview = useNumberPreview('doc.customs_declarations', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);

  // Поступления-импорты для выбора (только posted)
  const importReceiptsQ = useQuery({
    queryKey: ['goods-receipts-import', form.organization_id],
    queryFn: () => goodsReceipts.list({
      organization_id: form.organization_id, posted: true, limit: 200,
    }),
    enabled: !!form.organization_id,
  });

  // Когда выбрано поступление — подгружаем его строки
  const sourceReceiptQ = useQuery({
    queryKey: ['goods-receipt-full', form.source_receipt_id],
    queryFn: () => goodsReceipts.get(form.source_receipt_id),
    enabled: !!form.source_receipt_id,
  });

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : { ...f, organization_id: firstOrg });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        customs_number: d.customs_number,
        customs_date: d.customs_date,
        country_origin_code: d.country_origin_code ?? '',
        customs_fee: String(d.customs_fee),
        declaration_type: (d as { declaration_type?: 'IMPORT' | 'EXPORT' }).declaration_type ?? 'IMPORT',
        export_confirmation_date: (d as { export_confirmation_date?: string }).export_confirmation_date ?? '',
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          goods_receipt_item_id: it.goods_receipt_item_id,
          customs_value: String(it.customs_value),
          duty_amount: String(it.duty_amount),
          excise_amount: String(it.excise_amount),
          vat_amount: String(it.vat_amount),
          vat_rate_id: it.vat_rate_id ?? null,
          fee_allocated: it.fee_allocated,
          cost_addition: it.cost_addition,
        })),
        source_receipt_id: '',
      });
    }
  }, [isNew, docQuery.data, availableOrgs]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  // Карта nomenclature_id -> name
  const nomName = useMemo(() => {
    const m = new Map<string, string>();
    nomList.data?.items.forEach((n) => m.set(n.id, n.name));
    return m;
  }, [nomList.data]);

  // Карта goods_receipt_item_id -> { nomenclature_id, quantity } (агрегируем из загруженных
  // поступлений; для уже сохранённого ВМД нужна отдельная подгрузка строк поступлений)
  const [riCache, setRiCache] = useState<Record<string, { nom: string; qty: string; amount: string }>>({});

  useEffect(() => {
    if (sourceReceiptQ.data) {
      setRiCache((c) => {
        const next = { ...c };
        for (const it of sourceReceiptQ.data!.items) {
          if (it.id) {
            next[it.id] = {
              nom: it.nomenclature_id,
              qty: it.quantity,
              amount: it.amount_without_vat,
            };
          }
        }
        return next;
      });
    }
  }, [sourceReceiptQ.data]);

  const totals = useMemo(() => {
    let cv = 0, d = 0, e = 0, v = 0;
    for (const it of form.items) {
      cv += Number(it.customs_value || 0);
      d  += Number(it.duty_amount || 0);
      e  += Number(it.excise_amount || 0);
      v  += Number(it.vat_amount || 0);
    }
    const fee = Number(form.customs_fee || 0);
    return {
      cv, d, e, v, fee,
      addition: d + e + fee,
    };
  }, [form.items, form.customs_fee]);

  const create = useMutation({
    mutationFn: () => customsDeclarations.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      customs_number: form.customs_number,
      customs_date: form.customs_date,
      country_origin_code: form.country_origin_code || null,
      customs_fee: form.customs_fee || '0',
      declaration_type: form.declaration_type,
      comment: form.comment || null,
      items: form.items.map((it) => ({
        line_number: it.line_number,
        goods_receipt_item_id: it.goods_receipt_item_id,
        customs_value: it.customs_value,
        duty_amount: it.duty_amount,
        excise_amount: it.excise_amount,
        vat_amount: it.vat_amount,
        vat_rate_id: it.vat_rate_id ?? null,
      })),
    } as unknown as Parameters<typeof customsDeclarations.create>[0]),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['customs-declarations'] });
      navigate(`/customs-declarations/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => customsDeclarations.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      customs_number: form.customs_number,
      customs_date: form.customs_date,
      country_origin_code: form.country_origin_code || null,
      customs_fee: form.customs_fee || '0',
      comment: form.comment || null,
      items: form.items.map((it) => ({
        line_number: it.line_number,
        goods_receipt_item_id: it.goods_receipt_item_id,
        customs_value: it.customs_value,
        duty_amount: it.duty_amount,
        excise_amount: it.excise_amount,
        vat_amount: it.vat_amount,
        vat_rate_id: it.vat_rate_id ?? null,
      })),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customs-declaration', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: зберегти та провести одним кліком (create → post).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await customsDeclarations.create({
        organization_id: form.organization_id,
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        customs_number: form.customs_number,
        customs_date: form.customs_date,
        country_origin_code: form.country_origin_code || null,
        customs_fee: form.customs_fee || '0',
        declaration_type: form.declaration_type,
        comment: form.comment || null,
        items: form.items.map((it) => ({
          line_number: it.line_number,
          goods_receipt_item_id: it.goods_receipt_item_id,
          customs_value: it.customs_value,
          duty_amount: it.duty_amount,
          excise_amount: it.excise_amount,
          vat_amount: it.vat_amount,
          vat_rate_id: it.vat_rate_id ?? null,
        })),
      } as unknown as Parameters<typeof customsDeclarations.create>[0]);
      await customsDeclarations.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['customs-declarations'] });
      navigate(`/customs-declarations/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: спершу зберегти правки, потім провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await customsDeclarations.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        customs_number: form.customs_number,
        customs_date: form.customs_date,
        country_origin_code: form.country_origin_code || null,
        customs_fee: form.customs_fee || '0',
        comment: form.comment || null,
        items: form.items.map((it) => ({
          line_number: it.line_number,
          goods_receipt_item_id: it.goods_receipt_item_id,
          customs_value: it.customs_value,
          duty_amount: it.duty_amount,
          excise_amount: it.excise_amount,
          vat_amount: it.vat_amount,
          vat_rate_id: it.vat_rate_id ?? null,
        })),
      });
      await customsDeclarations.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customs-declaration', id] });
      qc.invalidateQueries({ queryKey: ['customs-declarations'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => customsDeclarations.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customs-declaration', id] });
      qc.invalidateQueries({ queryKey: ['customs-declarations'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => customsDeclarations.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customs-declarations'] });
      navigate('/customs-declarations');
    },
  });

  function setItem(i: number, patch: Partial<CustomsDeclarationItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => idx === i ? { ...e, ...patch } : e),
    }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }
  function addReceiptItem(itemId: string) {
    if (!sourceReceiptQ.data) return;
    const ri = sourceReceiptQ.data.items.find((x) => x.id === itemId);
    if (!ri) return;
    // Не добавляем дважды
    if (form.items.some((x) => x.goods_receipt_item_id === itemId)) return;
    const cv = ri.amount_without_vat;
    // По умолчанию ставим 20% (стандартный импортный НДС)
    const defaultVat = vatList.data?.find((v) => Number(v.rate_percent) === 20);
    setForm((f) => ({
      ...f,
      items: [...f.items, {
        line_number: f.items.length + 1,
        goods_receipt_item_id: itemId,
        customs_value: cv,
        duty_amount: '0',
        excise_amount: '0',
        vat_amount: '0',
        vat_rate_id: defaultVat?.id ?? null,
      }],
    }));
  }

  // UX β: warning якщо новий документ а user не має purchases.receipt.create у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: '#fef3c7', borderRadius: 6,
            border: '1px solid #fbbf24', color: '#92400e',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати ВМД у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/customs-declarations')}>Скасувати</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова митна декларація' : `ВМД: ${form.customs_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ проведено
            </span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/customs-declarations')}
          onSave={() => isNew ? create.mutate() : update.mutate()}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => isNew ? createAndPost.mutate() : updateAndPost.mutate()}
          postDisabled={!isNew && form.items.length === 0}
          postPending={create.isPending || createAndPost.isPending || updateAndPost.isPending || update.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/customs-declarations/${id}/print`)}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Організація</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>№ документа (внутр.)</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Дата</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
      </div>

      <div style={{
        marginTop: '0.5rem', padding: '0.5rem', background: '#f1f5f9',
        borderRadius: 6, display: 'flex', gap: '1.5rem', alignItems: 'center',
      }}>
        <strong>Тип ВМД:</strong>
        <label>
          <input type="radio" name="decl_type" value="IMPORT"
                 checked={form.declaration_type === 'IMPORT'}
                 disabled={isPosted || !isNew}
                 onChange={() => setForm({ ...form, declaration_type: 'IMPORT' })} />
          {' '}Імпорт
        </label>
        <label>
          <input type="radio" name="decl_type" value="EXPORT"
                 checked={form.declaration_type === 'EXPORT'}
                 disabled={isPosted || !isNew}
                 onChange={() => setForm({ ...form, declaration_type: 'EXPORT' })} />
          {' '}Експорт
        </label>
        {form.declaration_type === 'EXPORT' && form.export_confirmation_date && (
          <span style={{ color: '#15803d', marginLeft: 'auto' }}>
            ✓ Підтверджено: {form.export_confirmation_date}
          </span>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Номер ВМД (від митниці)</label>
          <input value={form.customs_number}
                 onChange={(e) => setForm({ ...form, customs_number: e.target.value })}
                 disabled={isPosted}
                 placeholder="напр., UA100000.2026.000001" />
        </div>
        <div className="field">
          <label>Дата ВМД</label>
          <DatePopover value={form.customs_date}
                 onChange={(v) => setForm({ ...form, customs_date: v })}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Країна походження</label>
          <input value={form.country_origin_code}
                 onChange={(e) => setForm({ ...form, country_origin_code: e.target.value })}
                 disabled={isPosted}
                 placeholder="DE / CN / TR / ..." maxLength={10} />
        </div>
        <div className="field">
          <label>Збір за оформлення</label>
          <input type="number" step="0.01" value={form.customs_fee}
                 onChange={(e) => setForm({ ...form, customs_fee: e.target.value })}
                 disabled={isPosted}
                 style={{ textAlign: 'right' }} />
        </div>
      </div>

      {!isPosted && (
        <div style={{
          marginTop: '1rem', padding: '0.75rem', background: '#f8fafc', borderRadius: 6,
          display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.75rem',
        }}>
          <div className="field">
            <label>Додати з поступлення</label>
            <select value={form.source_receipt_id}
                    onChange={(e) => setForm({ ...form, source_receipt_id: e.target.value })}>
              <option value="">— оберіть проведене поступлення —</option>
              {importReceiptsQ.data?.items.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.document_number} від {new Date(r.document_date).toLocaleDateString('uk-UA')}
                  {' — '}{fmt(Number(r.total_amount))} грн
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Рядки поступлення</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
              {sourceReceiptQ.data?.items.map((ri) => {
                const used = form.items.some((x) => x.goods_receipt_item_id === ri.id);
                return (
                  <button key={ri.id} type="button"
                          onClick={() => ri.id && addReceiptItem(ri.id)}
                          disabled={used}
                          style={{
                            padding: '0.3rem 0.6rem', fontSize: '0.85rem',
                            opacity: used ? 0.4 : 1,
                          }}>
                    {used ? '✓ ' : '+ '}
                    {nomName.get(ri.nomenclature_id) ?? 'товар'} ({ri.quantity})
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <h3 style={{ marginTop: '1.5rem' }}>Рядки ВМД</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 30 }}>#</th>
            <th style={{ padding: '0.5rem' }}>Товар (з поступлення)</th>
            <th style={{ padding: '0.5rem', width: 120 }}>Митна вартість</th>
            <th style={{ padding: '0.5rem', width: 100 }}>Мито</th>
            <th style={{ padding: '0.5rem', width: 100 }}>Акциз</th>
            <th style={{ padding: '0.5rem', width: 100 }}>ПДВ</th>
            {isPosted && <th style={{ padding: '0.5rem', width: 100 }}>+ собівартості</th>}
            <th style={{ padding: '0.5rem', width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => {
            const ri = riCache[it.goods_receipt_item_id];
            const label = ri
              ? `${nomName.get(ri.nom) ?? 'товар'} × ${ri.qty}`
              : it.goods_receipt_item_id.slice(0, 8) + '…';
            return (
              <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
                <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
                <td style={{ padding: '0.5rem', color: '#475569' }}>{label}</td>
                <td style={{ padding: '0.5rem' }}>
                  <input type="number" step="0.01" value={it.customs_value}
                         onChange={(e) => setItem(i, { customs_value: e.target.value })}
                         disabled={isPosted}
                         style={{ textAlign: 'right', width: '100%' }} />
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <input type="number" step="0.01" value={it.duty_amount}
                         onChange={(e) => setItem(i, { duty_amount: e.target.value })}
                         disabled={isPosted}
                         style={{ textAlign: 'right', width: '100%' }} />
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <input type="number" step="0.01" value={it.excise_amount}
                         onChange={(e) => setItem(i, { excise_amount: e.target.value })}
                         disabled={isPosted}
                         style={{ textAlign: 'right', width: '100%' }} />
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <input type="number" step="0.01" value={it.vat_amount}
                         onChange={(e) => setItem(i, { vat_amount: e.target.value })}
                         disabled={isPosted}
                         style={{ textAlign: 'right', width: '100%' }} />
                </td>
                {isPosted && (
                  <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>
                    {fmt(Number(it.cost_addition ?? 0))}
                  </td>
                )}
                <td style={{ padding: '0.5rem' }}>
                  {!isPosted && (
                    <button onClick={() => removeRow(i)} className="danger"
                            style={{ padding: '0.3rem 0.6rem' }}>×</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 600 }}>
            <td colSpan={2} style={{ padding: '0.5rem', textAlign: 'right' }}>Усього:</td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totals.cv)}</td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totals.d)}</td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totals.e)}</td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totals.v)}</td>
            {isPosted && <td></td>}
            <td></td>
          </tr>
        </tfoot>
      </table>

      {!isPosted && (
        <p style={{ marginTop: '0.5rem', color: '#64748b', fontSize: '0.85rem' }}>
          У собівартість піде: мито ({fmt(totals.d)}) + акциз ({fmt(totals.e)})
          + збір за оформлення ({fmt(totals.fee)}) = <strong>{fmt(totals.addition)}</strong> грн.
          ПДВ ({fmt(totals.v)}) → податковий кредит (рах. 6442), у собівартість НЕ йде.
        </p>
      )}

      <div className="field" style={{ marginTop: '1rem' }}>
        <label>Коментар</label>
        <input value={form.comment}
               onChange={(e) => setForm({ ...form, comment: e.target.value })}
               disabled={isPosted} />
      </div>

      {error && <div className="error">{error}</div>}

      {!isPosted && (
        <p style={{ marginTop: '1rem', color: '#64748b', fontSize: '0.85rem' }}>
          <strong>Як працює:</strong> при проведенні збір за оформлення розподіляється
          по рядках пропорційно митній вартості. Для кожного рядка:
          обчислюється додавання = мито + акциз + частина збору, оновлюється
          собівартість партії (батча, створеного відповідним поступленням),
          створюються проводки <strong>Дт 281 — Кт 6851</strong> (товари / митниця)
          та <strong>Дт 6442 — Кт 6851</strong> на ПДВ. У рядку поступлення
          автоматично проставляється номер ВМД і країна походження.
          Розпровести можна лише якщо з партії ще нічого не списано.
        </p>
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
